import React, { useEffect, useRef, useState } from 'react';
import { styles } from './CouponModal.css';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import PromoTag from '@jbhi-fi/lanyard-ui/components/PromoTag';
import { Delete, ScrollDownArrow, TickOutlined } from '@jbhi-fi/lanyard-ui/components/Icons';
import { CouponContent, CouponOffering } from '../types';
import { BarcodeContainer } from './BarcodeContainer';
import Tag from '@jbhi-fi/lanyard-ui/components/Tag/Tag';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { CouponProgressBanner } from '../CouponProgressBanner/CouponProgressBanner';
import { AddToWalletUrls } from './CouponModal';

type CouponModalContentProps = {
  couponCode: string;
  couponExternalId: number;
  expiryDate?: string;
  isLoading: boolean;
  isApplied: boolean;
  playCouponAppliedAnimation?: boolean;
  redemptionCondition?: 'instore' | 'online';
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: (code: string) => void;
  couponOffering?: CouponOffering;
  setIsQrModalOpen: (isOpen: boolean) => void;
  setClickedWallet: (wallet: 'apple' | 'google' | null) => void;
  addToWalletUrls?: AddToWalletUrls;
  addToWalletErrorCode?: number;
  shouldShowAddToWalletButtons?: boolean;
} & CouponContent;

export const CouponModalContent = ({
  displayName,
  couponCode,
  expiryDate,
  actionsLabel,
  barcodeLabel,
  barcode,
  additonalInstructions,
  image,
  termsAndConditions,
  isLoading,
  isApplied,
  redemptionCondition,
  couponOffering,
  onApplyCoupon,
  onRemoveCoupon,
  setIsQrModalOpen,
  addToWalletUrls,
  addToWalletErrorCode,
  setClickedWallet,
  shouldShowAddToWalletButtons,
}: CouponModalContentProps) => {
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [isBarcodeVisible, setIsBarcodeVisible] = useState(false);
  const buttonLabel = isApplied ? 'Applied' : 'Apply coupon to My Cart';
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);
  const handleRemoveCoupon = async (couponCode: string) => {
    setIsRemovingCoupon(true);
    await onRemoveCoupon(couponCode);
    setIsRemovingCoupon(false);
  };

  const defaultActionsLabel = redemptionCondition === 'instore' ? 'Redeem in store' : 'Redeem online';

  useEffect(() => {
    if (actionsRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsBarcodeVisible(entry.isIntersecting);
        },
        {
          threshold: 0.4,
        }
      );
      observer.observe(actionsRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.banner}>
            <div className={styles.iconContainer}>
              {image ? (
                <img src={image.src} alt={image.alt} />
              ) : (
                <Icon name="PerksMascotIllustrated" height={89} width={110} />
              )}
            </div>
            <CalloutFont as="h2" className={styles.iconHeading}>
              {displayName}
            </CalloutFont>
            {expiryDate ? <PromoTag label={`Expires: ${expiryDate}`} variant="main" /> : null}
          </div>
          {couponOffering?.coupon.isApplied && couponOffering?.coupon ? (
            <CouponProgressBanner
              variant="mini"
              title={couponOffering.coupon.content.displayName}
              expiryDate={couponOffering.coupon.content.expiryDateFormatted ?? couponOffering.coupon.expiryDate}
              isApplied={couponOffering.coupon.isApplied}
              discount={couponOffering.coupon.discount}
              targetValue={couponOffering.coupon.minimumSpend}
              value={couponOffering.currentSpend}
              hasWarning={couponOffering.isProductExcluded}
            />
          ) : null}
          {redemptionCondition ? <RedemptionConditionTag condition={redemptionCondition} /> : null}
          {termsAndConditions ? <div className={styles.terms}>{termsAndConditions}</div> : null}
        </div>
        <div className={styles.actions} ref={actionsRef}>
          {redemptionCondition !== 'instore' ? (
            <div className={styles.actionsContainer}>
              <h5 className={styles.actionsLabel}>{actionsLabel || defaultActionsLabel}</h5>
              <>
                <Button
                  classes={styles.actionsButton}
                  onClick={() => onApplyCoupon(couponCode)}
                  type="action"
                  icon={isApplied ? <TickOutlined /> : undefined}
                  label={buttonLabel}
                  loading={isLoading}
                  disabled={isApplied}
                  iconPosition="left"
                />
                {isApplied ? (
                  <Button
                    type="linkWithIcon"
                    icon={<Delete />}
                    label="Remove"
                    loading={isRemovingCoupon}
                    onClick={() => handleRemoveCoupon(couponCode)}
                    iconPosition="left"
                    classes={styles.deleteIcon}
                  />
                ) : null}
              </>
            </div>
          ) : null}

          {redemptionCondition !== 'online' ? (
            <BarcodeContainer
              barcode={barcode}
              barcodeLabel={barcodeLabel}
              couponCode={couponCode}
              additonalInstructions={additonalInstructions}
              setIsQrModalOpen={setIsQrModalOpen}
              setClickedWallet={setClickedWallet}
              addToWalletUrls={addToWalletUrls}
              addToWalletErrorCode={addToWalletErrorCode}
              shouldShowAddToWalletButtons={shouldShowAddToWalletButtons}
            />
          ) : null}
        </div>
      </div>
      {!isBarcodeVisible && (
        <div className={styles.scrollIndicator({ showScrollIndicator: !isBarcodeVisible })}>
          <div className={styles.scrollIndicatorBackground}></div>
          <div className={styles.scrollIndicatorTextContainer}>
            <ScrollDownArrow /> Scroll to redeem
          </div>
        </div>
      )}
    </>
  );
};

const RedemptionConditionTag = ({ condition }: { condition: 'instore' | 'online' }) => {
  const redemptionConditionTags = {
    instore: 'In-store only',
    online: 'Online only',
  };

  const label = redemptionConditionTags[condition];

  return <Tag className={styles.redmptionCondition} content={label} type="yellow" />;
};
